import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupComparison — deterministic cross-unit comparison data for ONE group
// (Ship 7). The comparison analogue of useGroupReorder: a service-role
// edge-fn read (get-group-comparison-snapshot) gated by resolveGroupOwner, so
// the group OWNER sees every unit's metrics even when they aren't a member of
// that unit.
//
// WINDOW-AWARE: `window` ('today' | '7d' | '30d' | '90d') is sent in the body
// and lives in the fetch deps, so changing the window re-fires the read.
//
// Returns:
//   units            — [{ restaurant_id, restaurant_name, metrics:{...} }]
//   biggestGapMetric — { metric, leader_unit, laggard_unit, ... } | null
//   leaderPerMetric  — { metric_key: unit_name }
//   laggardPerMetric — { metric_key: unit_name }
//   loading, error, refresh
//
// DECOUPLED from the AI brief (useGroupComparisonBrief): the table renders
// from these rows whether or not the brief has loaded. No cache — a plain read
// each time, same as useGroupReorder.

export function useGroupComparison(groupId, window = '7d') {
  const [units, setUnits]                 = useState([])
  const [biggestGapMetric, setBiggestGap] = useState(null)
  const [leaderPerMetric, setLeader]      = useState({})
  const [laggardPerMetric, setLaggard]    = useState({})
  const [loading, setLoading]             = useState(true)
  const [error, setError]                 = useState(null)

  // Synchronous in-flight guard — set before the first await, cleared in
  // finally. Stops two quick mounts/window-flips from double-firing.
  const inFlightRef = useRef(false)

  const fetchComparison = useCallback(async () => {
    if (!groupId) {
      setUnits([])
      setBiggestGap(null)
      setLeader({})
      setLaggard({})
      setLoading(false)
      setError(null)
      return
    }
    if (inFlightRef.current) return

    inFlightRef.current = true
    setLoading(true)
    setError(null)
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const resp = await fetch(`${supabaseUrl}/functions/v1/get-group-comparison-snapshot`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify({ group_id: groupId, window }),
      })
      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const payload = await resp.json()
      if (payload?.error) {
        throw new Error(payload.message || 'Could not load group comparison data.')
      }
      setUnits(Array.isArray(payload?.units) ? payload.units : [])
      setBiggestGap(payload?.biggest_gap_metric ?? null)
      setLeader(payload?.leader_unit_per_metric && typeof payload.leader_unit_per_metric === 'object' ? payload.leader_unit_per_metric : {})
      setLaggard(payload?.laggard_unit_per_metric && typeof payload.laggard_unit_per_metric === 'object' ? payload.laggard_unit_per_metric : {})
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useGroupComparison] comparison fetch failed', e)
      setError('Could not load group comparison data.')
      setUnits([])
      setBiggestGap(null)
      setLeader({})
      setLaggard({})
    } finally {
      inFlightRef.current = false
      setLoading(false)
    }
  }, [groupId, window])

  // Initial load + re-fire whenever groupId or window changes.
  useEffect(() => {
    fetchComparison()
  }, [fetchComparison])

  return useMemo(
    () => ({
      units,
      biggestGapMetric,
      leaderPerMetric,
      laggardPerMetric,
      loading,
      error,
      refresh: () => fetchComparison(),
    }),
    [units, biggestGapMetric, leaderPerMetric, laggardPerMetric, loading, error, fetchComparison],
  )
}
